/* eslint-disable max-lines, import/max-dependencies */
import { get } from "@src/appV2/api/parsedApi";
import { environmentConfig } from "@src/appV2/environment";
import { useGetFacilityProfile } from "@src/appV2/FacilityProfile/api/useGetFacilityProfile";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { logEvent } from "@src/appV2/lib/analytics/log";
import { getServerNow } from "@src/appV2/lib/clockOffset";
import { type Money } from "@src/appV2/lib/Money/types";
import { invalidateSpendingControlsQueries } from "@src/appV2/redesign/SpendingControls/api/invalidateSpendingControlsQueries";
import { type RushFeeInfo } from "@src/appV2/redesign/utils/rushFee";
import { useCreateShiftInvite } from "@src/appV2/ShiftInvites/api/useCreateShiftInvite";
import { useValidateShiftInvite } from "@src/appV2/ShiftInvites/api/useValidateShiftInvite";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { subDays } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { PENDING_INVITES_QUERY_KEY_PREFIX } from "../../../api/useGetPendingInvites";
import { useRushFeeCheck } from "../../../hooks/useRushFeeCheck";
import {
  type InviteBlockReason,
  type InviteErrorReason,
  type InviteFlowStatus,
  type InviteWarningReason,
  type Professional,
  type ShiftDetails,
  type ValidationResult,
  WORKER_STATUS,
} from "../types";
import {
  formatNextValidSlot,
  getInviteValidationError,
  getNextValidSlot,
  getStartTimeError,
  isSlotBlocked,
} from "./inviteFlowUtils";
import { type ChargeConstraints, useInviteChargeRate } from "./useInviteChargeRate";

const attendanceProfileResponseSchema = z.object({
  performanceMetrics: z
    .object({
      completedShifts: z.number(),
      totalNCNS: z.number(),
      shortNoticeCancellations: z.number(),
    })
    .optional(),
});

type AttendanceProfile = z.infer<typeof attendanceProfileResponseSchema>;

function getCallOffCount(profile: AttendanceProfile | undefined): number {
  if (!profile) {
    return 0;
  }
  const totalNCNS = profile.performanceMetrics?.totalNCNS ?? 0;
  const shortNoticeCancellations = profile.performanceMetrics?.shortNoticeCancellations ?? 0;
  return Math.max(totalNCNS, shortNoticeCancellations);
}

function checkHighCallOff(
  profile: AttendanceProfile | undefined,
  maxAllowedCallOffs?: number,
  maxAllowedPercentageOfCallOffs?: number
): boolean {
  if (
    !profile ||
    maxAllowedCallOffs === undefined ||
    maxAllowedPercentageOfCallOffs === undefined
  ) {
    return false;
  }
  const completedShifts = Math.max(profile.performanceMetrics?.completedShifts ?? 0, 1);
  const totalCallOffs = getCallOffCount(profile);
  return (
    totalCallOffs >= maxAllowedCallOffs ||
    totalCallOffs / completedShifts >= maxAllowedPercentageOfCallOffs
  );
}

interface UseInviteFlowOptions {
  shiftDetails: ShiftDetails;
  preselectedProfessional?: Professional;
  onSuccess?: () => void;
}

interface UseInviteFlowReturn {
  status: InviteFlowStatus;
  selectedProfessional: Professional | undefined;
  validationResult: ValidationResult | undefined;
  postToMarketplace: boolean;
  blockReason: InviteBlockReason | undefined;
  canSendInvite: boolean;
  errorReasons: InviteErrorReason[];
  warningReasons: InviteWarningReason[];
  missingDocuments: string[] | undefined;
  errorDetail: string | undefined;
  rushFeeInfo: RushFeeInfo | undefined;
  startTimeError: string | undefined;
  errorModalState: ReturnType<typeof useModalState>;
  blockedModalState: ReturnType<typeof useModalState>;
  baseChargeRate: Money | undefined;
  chargeConstraints: ChargeConstraints | undefined;
  customChargeRateBoost: number | undefined;
  isCustomRateBoostEnabled: boolean;
  isPricingLoading: boolean;
  selectProfessional: (professional: Professional) => void;
  clearSelection: () => void;
  setPostToMarketplace: (value: boolean) => void;
  setCustomChargeRateBoost: (boost: number | undefined) => void;
  sendInvite: () => Promise<void>;
  reset: () => void;
  checkIfBlocked: () => boolean;
  dismissError: () => void;
}

export function useInviteFlow(options: UseInviteFlowOptions): UseInviteFlowReturn {
  const { shiftDetails, preselectedProfessional, onSuccess } = options;
  const [status, setStatus] = useState<InviteFlowStatus>(
    preselectedProfessional ? "selecting" : "idle"
  );
  const [selectedProfessional, setSelectedProfessional] = useState<Professional | undefined>(
    preselectedProfessional
  );
  const postToMarketplaceDefault = useCbhFlag(
    CbhFeatureFlag.SHIFT_INVITE_POST_TO_MARKETPLACE_DEFAULT,
    {
      defaultValue: true,
    }
  );
  const [postToMarketplace, setPostToMarketplace] = useState(postToMarketplaceDefault);

  useEffect(() => {
    setPostToMarketplace(postToMarketplaceDefault);
  }, [postToMarketplaceDefault]);

  const [validationResult, setValidationResult] = useState<ValidationResult | undefined>();
  // Ref mirror so callbacks can read the latest validation state without
  // re-binding on every change (and reading a stale closure value).
  const validationResultRef = useRef<ValidationResult | undefined>(validationResult);
  validationResultRef.current = validationResult;
  const [blockReason, setBlockReason] = useState<InviteBlockReason | undefined>();
  const errorModalState = useModalState(ModalStatus.CLOSED);
  const blockedModalState = useModalState(ModalStatus.CLOSED);
  const validateMutation = useValidateShiftInvite();
  const createInviteMutation = useCreateShiftInvite();
  const rushFeeCheck = useRushFeeCheck({ startTime: shiftDetails.startTime });
  const queryClient = useQueryClient();
  const isSendingRef = useRef(false);

  // The facilityprofile endpoint strips fields not listed in the projection,
  // so we must explicitly request `restrictOverworkShiftInvites` — otherwise
  // it's silently undefined and the workplace toggle has no effect on classification.
  const { data: facilityProfile } = useGetFacilityProfile(
    shiftDetails.workplaceId,
    "restrictOverworkShiftInvites"
  );
  const restrictOverworkShiftInvites = facilityProfile?.restrictOverworkShiftInvites ?? false;

  const isFacilityDocumentsOverrideEnabled = useCbhFlag(
    CbhFeatureFlag.OVERRIDE_FACILITY_REQUIRED_DOCS_FOR_SHIFT_INVITE,
    { defaultValue: true }
  );

  const frictionConfig = useCbhFlag(CbhFeatureFlag.SHIFT_INVITE_FRICTION_CONFIG, {
    defaultValue: { enabled: false as const },
  });

  const withPerformanceSince = useMemo(() => {
    if (
      frictionConfig.enabled &&
      "numberOfPastDaysToEvaluate" in frictionConfig &&
      frictionConfig.numberOfPastDaysToEvaluate !== undefined
    ) {
      return subDays(getServerNow(), frictionConfig.numberOfPastDaysToEvaluate).toISOString();
    }
    return undefined;
  }, [frictionConfig]);

  const attendanceProfileQuery = useQuery({
    queryKey: ["workerAttendanceProfile", selectedProfessional?.id, withPerformanceSince],
    queryFn: async () => {
      const baseUrl = `${environmentConfig.REACT_APP_ATTENDANCE_SCORE_SERVICE_URL}/workers/${selectedProfessional?.id}/profile`;
      const url = withPerformanceSince
        ? `${baseUrl}?withPerformanceSince=${encodeURIComponent(withPerformanceSince)}`
        : baseUrl;
      const response = await get({
        url,
        responseSchema: attendanceProfileResponseSchema,
      });
      return response.data;
    },
    enabled: Boolean(selectedProfessional && frictionConfig.enabled),
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 10_000,
  });
  const { data: attendanceProfileData, refetch: refetchAttendanceProfile } = attendanceProfileQuery;

  const {
    baseChargeRate,
    chargeConstraints,
    customChargeRateBoost,
    isCustomRateBoostEnabled,
    isPricingLoading,
    setCustomChargeRateBoost,
    resetChargeRate,
  } = useInviteChargeRate({ shiftDetails, selectedProfessional });

  // Use ref to always have latest onSuccess callback (avoids stale closure)
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // Chat or My-Professionals flows can pre-select a worker who is already
  // blocked at this workplace. Surface the WORKER_BLOCKED error up-front so the
  // admin understands why the send button is blocked, rather than relying on a
  // server-side rejection after pressing Send (ORB-1749).
  const hasHandledPreselectBlockRef = useRef(false);
  useEffect(() => {
    if (hasHandledPreselectBlockRef.current) {
      return;
    }
    if (preselectedProfessional?.statusLabel === WORKER_STATUS.blocked) {
      hasHandledPreselectBlockRef.current = true;
      setValidationResult({
        isValid: false,
        errorReasons: ["WORKER_BLOCKED"],
        warningReasons: [],
      });
      setStatus("error");
      errorModalState.openModal();
    }
  }, [preselectedProfessional, errorModalState]);

  const checkIfBlocked = useCallback((): boolean => {
    if (isSlotBlocked(shiftDetails.timeSlot, shiftDetails.startTime, shiftDetails.timezone)) {
      const nextSlot = getNextValidSlot(shiftDetails.timeSlot, shiftDetails.startTime);
      setBlockReason({
        type: "past-slot",
        message: `You can't send invites for shifts starting in the past. You can send invites starting ${formatNextValidSlot(
          nextSlot.date,
          nextSlot.slot
        )}.`,
        nextValidSlot: nextSlot,
      });
      setStatus("blocked");
      blockedModalState.openModal();
      return true;
    }
    // Slot is now valid — drop the block state so a previously-blocked flow
    // can recover when the user picks a different slot. Only resets status if
    // we were blocked; legitimate transitions like "valid" / "sending" stay.
    setBlockReason(undefined);
    setStatus((previous) => (previous === "blocked" ? "idle" : previous));
    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useModalState's return ref churns every render; including blockedModalState would make checkIfBlocked unstable and loop the consumer useEffect. openModal proxies to a stable setState, so a stale-closure reference is harmless.
  }, [shiftDetails.timeSlot, shiftDetails.startTime, shiftDetails.timezone]);

  const startTimeError = useMemo(
    () => getStartTimeError(shiftDetails.startTime),
    [shiftDetails.startTime]
  );

  const { rushFeeInfo } = rushFeeCheck;

  const canSendInvite = useMemo(
    () =>
      selectedProfessional !== undefined &&
      startTimeError === undefined &&
      // Gate directly on slot validity: `status` can be clobbered by
      // `selectProfessional` setting "selecting", which would otherwise let a
      // previously-blocked flow send after the user swaps the worker.
      blockReason === undefined &&
      // Block re-entry while a validate/create round-trip is in flight.
      // Without this, a second click could spawn a parallel validate that
      // resolves after the first create completes and fires another create
      // for the same worker/shift.
      status !== "validating" &&
      status !== "valid" &&
      status !== "sending" &&
      status !== "sent" &&
      status !== "blocked" &&
      status !== "error",
    [selectedProfessional, startTimeError, blockReason, status]
  );

  const errorReasons = useMemo(() => validationResult?.errorReasons ?? [], [validationResult]);
  const warningReasons = useMemo(() => validationResult?.warningReasons ?? [], [validationResult]);
  const missingDocuments = useMemo(() => validationResult?.missingDocuments, [validationResult]);
  const errorDetail = useMemo(() => validationResult?.detail, [validationResult]);

  const selectProfessional = useCallback(
    (professional: Professional) => {
      // Defense-in-depth: the list item disables click for blocked workers, but
      // callers (e.g. a chat-derived pre-select) may still pass a blocked
      // professional. Route it through the error modal rather than letting the
      // validate call surface a generic "cannot change shift" error — which was
      // the original reported behavior in ORB-1749.
      if (professional.statusLabel === WORKER_STATUS.blocked) {
        setValidationResult({
          isValid: false,
          errorReasons: ["WORKER_BLOCKED"],
          warningReasons: [],
        });
        setStatus("error");
        errorModalState.openModal();
        return;
      }

      setSelectedProfessional(professional);
      setStatus("selecting");
      setValidationResult(undefined);
    },
    [errorModalState]
  );

  const clearSelection = useCallback(() => {
    setSelectedProfessional(undefined);
    setStatus("idle");
    setValidationResult(undefined);
    resetChargeRate();
  }, [resetChargeRate]);

  const dismissError = useCallback(() => {
    setStatus("selecting");
    setValidationResult(undefined);
  }, []);

  const buildShiftInviteRequest = useCallback(
    (workerId: string) => ({
      shiftInviteRequest: {
        workerId,
        workplaceId: shiftDetails.workplaceId,
        shiftDetails: {
          start: shiftDetails.startTime,
          end: shiftDetails.endTime,
          agentReq: shiftDetails.qualification,
          name: shiftDetails.timeSlot,
          tmz: shiftDetails.timezone,
          ...(customChargeRateBoost !== undefined ? { customChargeRateBoost } : {}),
        },
        postShiftToMarketplace: postToMarketplace,
      },
    }),
    [shiftDetails, postToMarketplace, customChargeRateBoost]
  );

  const validateProfessional = useCallback(async () => {
    if (!selectedProfessional) {
      return;
    }
    setStatus("validating");
    try {
      await validateMutation.mutateAsync(buildShiftInviteRequest(selectedProfessional.id));

      const warnings: InviteWarningReason[] = [];
      if (frictionConfig.enabled) {
        const maxCallOffs =
          "maxAllowedCallOffs" in frictionConfig ? frictionConfig.maxAllowedCallOffs : undefined;
        const maxPercentage =
          "maxAllowedPercentageOfCallOffs" in frictionConfig
            ? frictionConfig.maxAllowedPercentageOfCallOffs
            : undefined;
        const profileResult = attendanceProfileData ?? (await refetchAttendanceProfile()).data;
        if (checkHighCallOff(profileResult, maxCallOffs, maxPercentage)) {
          const totalCallOffs = getCallOffCount(profileResult);
          const completedShifts = Math.max(
            profileResult?.performanceMetrics?.completedShifts ?? 0,
            1
          );
          const calloffRate = Math.round((totalCallOffs / completedShifts) * 100);
          warnings.push({ type: "HIGH_CALLOFF_RATE", details: { calloffRate } });
        }
      }

      setValidationResult({ isValid: true, errorReasons: [], warningReasons: warnings });
      setStatus("valid");
    } catch (error: unknown) {
      const {
        errorReasons,
        warningReasons: apiWarningReasons,
        missingDocuments,
        detail,
      } = getInviteValidationError(error, {
        restrictOverworkShiftInvites,
        isFacilityDocumentsOverrideEnabled,
      });

      // Warning-only rejection (e.g. SHIFT_OVERWORKING when the workplace flag
      // is off, SHIFT_OVERWORKING_FROM_INVITES, SHIFT_OVERLAPPING). Surface as
      // an overrideable warning; the warning-modal effect picks it up.
      if (errorReasons.length === 0 && apiWarningReasons.length > 0) {
        setValidationResult({
          isValid: true,
          errorReasons: [],
          warningReasons: apiWarningReasons,
        });
        setStatus("valid");
        return;
      }

      // Mirror the legacy stepper: surface every reason at once. Blocking
      // errors gate the send, but overrideable warnings (overlap, overwork,
      // missing facility docs, ...) are listed alongside so the workplace
      // user can see the full picture rather than fix one issue at a time.
      setValidationResult({
        isValid: false,
        errorReasons,
        warningReasons: apiWarningReasons,
        missingDocuments,
        detail,
      });
      setStatus("error");
      errorModalState.openModal();
    }
  }, [
    selectedProfessional,
    buildShiftInviteRequest,
    validateMutation,
    errorModalState,
    frictionConfig,
    attendanceProfileData,
    refetchAttendanceProfile,
    restrictOverworkShiftInvites,
    isFacilityDocumentsOverrideEnabled,
  ]);

  const doSendInvite = useCallback(
    async (
      options: { allowOverwork?: boolean; skipFacilityRequiredDocumentCheck?: boolean } = {}
    ) => {
      if (!selectedProfessional || isSendingRef.current) {
        return;
      }
      const { allowOverwork = false, skipFacilityRequiredDocumentCheck = false } = options;
      isSendingRef.current = true;
      setStatus("sending");
      try {
        const queryParams = {
          ...(allowOverwork ? { isOverworkingShiftsAllowed: true } : {}),
          ...(skipFacilityRequiredDocumentCheck ? { skipFacilityRequiredDocumentCheck: true } : {}),
        };
        await createInviteMutation.mutateAsync({
          ...buildShiftInviteRequest(selectedProfessional.id),
          ...(Object.keys(queryParams).length > 0 ? { queryParams } : {}),
        });
        setStatus("sent");
        void queryClient.invalidateQueries({ queryKey: PENDING_INVITES_QUERY_KEY_PREFIX });
        invalidateSpendingControlsQueries(queryClient);

        try {
          logEvent(APP_V2_USER_EVENTS.SCHEDULE_INVITE_SENT, {
            workplaceId: shiftDetails.workplaceId,
            workerId: selectedProfessional.id,
            shiftStartTime: shiftDetails.startTime,
            shiftEndTime: shiftDetails.endTime,
            qualification: shiftDetails.qualification,
            postToMarketplace,
          });
        } catch {
          // Non-blocking analytics
        }

        onSuccessRef.current?.();
      } catch (error) {
        // eslint-disable-next-line no-console -- Debug logging for invite flow
        console.error("[useInviteFlow] doSendInvite error:", error);
        const {
          errorReasons,
          warningReasons: newWarningReasons,
          missingDocuments,
          detail,
        } = getInviteValidationError(error, {
          restrictOverworkShiftInvites,
          isFacilityDocumentsOverrideEnabled,
        });
        // Carry through warnings the user already acknowledged in the same flow
        // (e.g. doc-override) so the post-Send error modal shows the full picture
        // alongside any new reasons returned by the create endpoint, deduped by
        // warning type.
        const acknowledgedWarnings = validationResultRef.current?.warningReasons ?? [];
        const mergedWarnings = [
          ...acknowledgedWarnings,
          ...newWarningReasons.filter(
            (incoming) => !acknowledgedWarnings.some((existing) => existing.type === incoming.type)
          ),
        ];
        setValidationResult({
          isValid: false,
          errorReasons: errorReasons.length > 0 ? errorReasons : ["WORKER_CANT_CLAIM_SHIFT"],
          warningReasons: mergedWarnings,
          missingDocuments,
          detail,
        });
        setStatus("error");
        errorModalState.openModal();
      } finally {
        isSendingRef.current = false;
      }
    },
    [
      selectedProfessional,
      buildShiftInviteRequest,
      createInviteMutation,
      queryClient,
      shiftDetails.workplaceId,
      shiftDetails.startTime,
      shiftDetails.endTime,
      shiftDetails.qualification,
      postToMarketplace,
      errorModalState,
      restrictOverworkShiftInvites,
      isFacilityDocumentsOverrideEnabled,
    ]
  );

  const sendInvite = useCallback(async () => {
    if (!canSendInvite) {
      return;
    }
    await validateProfessional();
  }, [canSendInvite, validateProfessional]);

  // No separate "review the warnings" confirmation step. The admin has already
  // committed by clicking Send in the drawer, so when validate succeeds (with or
  // without overrideable warnings) we go straight to the create call. If create
  // itself fails, the error modal then opens with the full reason list — the
  // user only ever sees one modal, never a warning-then-error sequence.
  useEffect(() => {
    if (status === "valid" && validationResult?.isValid) {
      const allowOverwork = warningReasons.some(
        (warning) =>
          warning.type === "SHIFT_OVERWORKING" || warning.type === "SHIFT_OVERWORKING_FROM_INVITES"
      );
      const skipFacilityRequiredDocumentCheck = warningReasons.some(
        (warning) => warning.type === "MISSING_FACILITY_REQUIRED_DOCS"
      );
      void doSendInvite({ allowOverwork, skipFacilityRequiredDocumentCheck });
    }
  }, [status, validationResult?.isValid, warningReasons, doSendInvite]);

  const reset = useCallback(() => {
    setStatus("idle");
    setSelectedProfessional(undefined);
    setPostToMarketplace(postToMarketplaceDefault);
    setValidationResult(undefined);
    setBlockReason(undefined);
    isSendingRef.current = false;
  }, [postToMarketplaceDefault]);

  return {
    status,
    selectedProfessional,
    validationResult,
    postToMarketplace,
    blockReason,
    canSendInvite,
    errorReasons,
    warningReasons,
    missingDocuments,
    errorDetail,
    rushFeeInfo,
    startTimeError,
    errorModalState,
    blockedModalState,
    baseChargeRate,
    chargeConstraints,
    customChargeRateBoost,
    isCustomRateBoostEnabled,
    isPricingLoading,
    selectProfessional,
    clearSelection,
    setPostToMarketplace,
    setCustomChargeRateBoost,
    sendInvite,
    reset,
    checkIfBlocked,
    dismissError,
  };
}
/* eslint-enable max-lines, import/max-dependencies */
